import { Box, Divider, Grid } from '@material-ui/core'
import React, { ReactElement } from 'react'
import {
  DeleteWithConfirmButton,
  EditButton,
  FunctionField,
  ReferenceField,
  Show,
  ShowProps,
  Tab,
  TabbedShowLayout,
  TabbedShowLayoutTabs,
  TextField,
  TextInput,
  TitleProps,
  useShowContext,
  useTranslate,
} from 'react-admin'
import RelationTab from '../../../common/RelationTab'
import relationTabFilter from '../../../common/RelationTabFilter'
import { ResourceName } from '../../../../core/ResourceName'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import { Authority } from '../../../../core/auth/Authority'
import { EngineMediaFileDto } from '../../../../core/dto/engine-media/engine-media-file.dto'
import { EngineMediaPackDto } from '../../../../core/dto/engine-media/engine-media-pack.dto'
import { EngineMediaActionDto } from '../../../../core/dto/engine-media/engine-media-action.dto'

interface EngineMediaPackTitleProps extends TitleProps {
  record?: EngineMediaFileDto
}

const ShowTitle = (data: EngineMediaPackTitleProps) => {
  const { record: file } = data
  const translate = useTranslate()

  return (
    <span>
      {translate('resources.engine-media-packs.titles.show')}: {file?.id}
    </span>
  )
}

const GeneralActions = () => {
  const { record: pack } = useShowContext<EngineMediaPackDto>()
  const hasAuthority = useHasAuthority()

  return (
    <>
      <Divider />
      <Box p={1} display="flex" justifyContent="flex-end">
        <Grid container direction="row" justify="flex-end" alignItems="center">
          <Grid item style={{ margin: '2px' }}>
            <DeleteWithConfirmButton
              basePath={`/${ResourceName.ENGINE_MEDIA_PACKS}`}
              record={pack}
              variant="contained"
              disabled={!hasAuthority(Authority.DELETE_ENGINE_MEDIA_PACKS)}
            />
          </Grid>
          <Grid item>
            <EditButton
              variant="contained"
              basePath="/engine-media-packs"
              record={pack}
              style={{ margin: '2px' }}
              disabled={!hasAuthority(Authority.EDIT_ENGINE_MEDIA_PACKS)}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

const EngineMediaPackTabs = ({ ...props }) => {
  const translate = useTranslate()
  const { record: file } = props
  const hasAuthority = useHasAuthority()

  const relationFilters = (...filterProps) =>
    relationTabFilter({
      ...filterProps,
      children: [<TextInput source="description" alwaysOn />],
      resource: ResourceName.ENGINE_MEDIA_PACKS,
    })

  return (
    <TabbedShowLayout
      {...props}
      tabs={
        <TabbedShowLayoutTabs
          variant="scrollable"
          scrollButtons="auto"
          {...props}
        />
      }
    >
      <Tab label={translate('resources.engine-media-packs.tabs.general')}>
        <TextField source="id" label="ID" />
        <TextField source="description" />
        <GeneralActions />
      </Tab>
      {hasAuthority(Authority.VIEW_ENGINE_MEDIA_FILES) && (
        <Tab
          path="files"
          label={translate('resources.engine-media-packs.tabs.files')}
        >
          <>
            <RelationTab<EngineMediaFileDto>
              resource={ResourceName.ENGINE_MEDIA_FILES}
              source="engineMediaFilesIds"
              mode={
                hasAuthority(Authority.EDIT_ENGINE_MEDIA_PACKS)
                  ? 'edit'
                  : 'show'
              }
              attachMethod="attachFiles"
              detachMethod="detachFiles"
              attachRequestPayload={(r, ids) => ({
                engineMediaPackId: r.id,
                engineMediaFilesIds: ids,
              })}
              detachRequestPayload={(r, ids) => ({
                engineMediaPackId: r.id,
                engineMediaFilesIds: ids,
              })}
              refetchListAfterChange={(filters) =>
                filters?.filterPackId === true
              }
              filters={relationFilters() as ReactElement[]}
              filterDefaultValues={{
                packId: file?.id,
                filterPackId: false,
              }}
              sort={{
                field: 'id',
                order: 'DESC',
              }}
            >
              <TextField source="id" label="ID" />
              <TextField source="description" key="description" />
              {hasAuthority(Authority.VIEW_ENGINE_MEDIA_ACTIONS) && (
                <ReferenceField
                  source="engineMediaActionId"
                  reference={ResourceName.ENGINE_MEDIA_ACTIONS}
                  link="show"
                >
                  <FunctionField<EngineMediaActionDto>
                    render={(record?: EngineMediaActionDto) =>
                      `${record?.id}. ${record?.name} (${record?.mediaAction})`
                    }
                  />
                </ReferenceField>
              )}
            </RelationTab>
          </>
        </Tab>
      )}
    </TabbedShowLayout>
  )
}

export const EngineMediaPackShow = (props: ShowProps) => (
  <Show {...props} actions={false} title={<ShowTitle />}>
    <EngineMediaPackTabs />
  </Show>
)
